import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Card, CardContent, Grid, IconButton, Tooltip, TablePagination,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import BadgeIcon from '@mui/icons-material/Badge';
import PendingIcon from '@mui/icons-material/Pending';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const statusColors = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  rejected: 'error',
};

const typeIcons = {
  customer: <PersonIcon fontSize="small" />,
  shop: <StoreIcon fontSize="small" />,
  agent: <BadgeIcon fontSize="small" />,
};

const typeColors = {
  customer: '#2563EB',
  shop: '#FF6B00',
  agent: '#0F172A',
};

const AccountDeletionManagement = () => {
  const { can } = usePermissions();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, processing: 0, completed: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [loadError, setLoadError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminRemarks, setAdminRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const token = sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = { page: page + 1, limit: rowsPerPage };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.accountType = filterType;

      const res = await axios.get(`${API_BASE_URL}/account-deletion/requests`, { headers, params });
      setRequests(res.data.requests || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch deletion requests');
      setLoadError((err.response && err.response.data && err.response.data.message) || 'Could not load the deletion requests. Please log in again and retry.');
    }
    setLoading(false);
  }, [page, rowsPerPage, filterStatus, filterType]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/account-deletion/stats`, { headers });
      setStats(res.data.stats || {});
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests, fetchStats]);

  const handleAction = (request, action) => {
    if (!can('customers.deletion.approve')) return;
    setSelectedRequest(request);
    setDialogAction(action);
    setAdminRemarks('');
    setDialogOpen(true);
  };

  const confirmAction = async () => {
    if (!can('customers.deletion.approve')) return;
    if (!selectedRequest) return;
    setActionLoading(true);

    try {
      const endpoint = dialogAction === 'approve'
        ? `${API_BASE_URL}/account-deletion/${selectedRequest._id}/process`
        : `${API_BASE_URL}/account-deletion/${selectedRequest._id}/reject`;

      await axios.put(endpoint, { adminRemarks }, { headers });
      setDialogOpen(false);
      fetchRequests();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed');
    }
    setActionLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <BackButton />
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteIcon color="error" /> Account Deletion Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Pending', value: stats.pending, color: '#ED6C02', icon: <PendingIcon /> },
          { label: 'Completed', value: stats.completed, color: '#2E7D32', icon: <CheckCircleIcon /> },
          { label: 'Rejected', value: stats.rejected, color: '#D32F2F', icon: <CancelIcon /> },
          { label: 'Total', value: stats.total, color: '#1565C0', icon: <DeleteIcon /> },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ borderLeft: `4px solid ${stat.color}` }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color }}>{stat.value}</Typography>
                  </Box>
                  <Box sx={{ color: stat.color, opacity: 0.3 }}>{React.cloneElement(stat.icon, { sx: { fontSize: 40 } })}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Status" value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Account Type" value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="customer">Customer</MenuItem>
          <MenuItem value="shop">Shop</MenuItem>
          <MenuItem value="agent">Agent</MenuItem>
        </TextField>
        <Button variant="outlined" size="small" onClick={() => { setFilterStatus(''); setFilterType(''); setPage(0); }}>
          Clear Filters
        </Button>
      </Paper>

      {/* Table */}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sl.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Requested</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Processed</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Remarks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No deletion requests found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((req, idx) => (
                    <TableRow key={req._id} hover>
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>
                        <Chip
                          icon={typeIcons[req.accountType]}
                          label={req.accountType.toUpperCase()}
                          size="small"
                          sx={{ backgroundColor: typeColors[req.accountType] + '15', color: typeColors[req.accountType], fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        {req.name}
                        {req.accountType === 'customer' && req.customerNo && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                            Customer ID: {req.customerNo}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{req.mobile}</TableCell>
                      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.email || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.reason || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDate(req.requestedAt)}</TableCell>
                      <TableCell>
                        <Chip label={req.status.toUpperCase()} size="small" color={statusColors[req.status]} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDate(req.processedAt)}</TableCell>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.adminRemarks || '-'}</TableCell>
                      <TableCell>
                        {(req.status === 'pending' || req.status === 'processing') ? (
                          can('customers.deletion.approve') ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Approve & Delete Account">
                                <IconButton size="small" color="success" onClick={() => handleAction(req, 'approve')}>
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Request">
                                <IconButton size="small" color="error" onClick={() => handleAction(req, 'reject')}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )
                        ) : (
                          <Typography variant="caption" color="text.secondary">Done</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={total} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {dialogAction === 'approve' ? 'Approve Account Deletion' : 'Reject Deletion Request'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Account Type:</strong> {selectedRequest.accountType.toUpperCase()}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {selectedRequest.name}</Typography>
              <Typography variant="body2"><strong>Mobile:</strong> {selectedRequest.mobile}</Typography>
              {selectedRequest.email && <Typography variant="body2"><strong>Email:</strong> {selectedRequest.email}</Typography>}
              {selectedRequest.linkedRecordId && (
                <Chip label="Account Found in System" size="small" color="success" sx={{ mt: 1 }} />
              )}
              {!selectedRequest.linkedRecordId && (
                <Chip label="No Matching Account Found" size="small" color="warning" sx={{ mt: 1 }} />
              )}
            </Box>
          )}
          {dialogAction === 'approve' && (
            <Typography variant="body2" color="error" sx={{ mb: 2, p: 1, backgroundColor: '#FEF2F2', borderRadius: 1 }}>
              Warning: This will permanently anonymize the user's personal data. Transaction records will be retained for 5 years per PMLA compliance. This action cannot be undone.
            </Typography>
          )}
          <TextField
            fullWidth multiline rows={3} label="Admin Remarks"
            value={adminRemarks} onChange={(e) => setAdminRemarks(e.target.value)}
            placeholder={dialogAction === 'approve' ? 'Identity verified via...' : 'Reason for rejection...'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={dialogAction === 'approve' ? 'error' : 'warning'}
            onClick={confirmAction}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : dialogAction === 'approve' ? 'Delete Account' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDeletionManagement;
